import { PhoneInterface } from "../models";
import { UserI } from "./interfaces";


export interface PassedAdI {
  adType?: number;
  category?: string;
  cityId?: string;
  code?: number;
  countryId?: string;
  countyId?: string;
  creatorEmail?: string;
  creatorId?: string;
  categoryLv1Id?: string;
  categoryLv2Id?: string;
  description?: string;
  expirationDate?: string;
  expirationPeriod?: number;
  externalUrl?: string;
  floorPlans?: string[];
  id?: string;
  externalId?: string;
  images?: string[];
  is18Old?: false;
  isDeleted: boolean;
  isFavorite: boolean;
  isPromoted: boolean;
  promotionExpirationDate: string | null;
  promotionStartDate: string | null;
  promotionRemainingDays: number | null;
  promotionRemainingHours: number | null;
  awaitingPayment: boolean;
  autoPromotionStatus: number; // AdAutoPromotionStatus enum
  legalEntity: number;
  module?: string;
  moduleHr?: string;
  postedTime?: string;
  renewalTime?: string;
  price?: number;
  priceTo?: number;
  // BE now signals presence of phone numbers via a boolean; actual numbers
  // are fetched on demand through /api/phone/reveal/ad/{adId}.
  hasPhones?: boolean;
  priceM2?: number | null;
  averagePriceM2?: number | null;
  lowerRangePriceM2?: number | null;
  higherRangePriceM2?: number | null;
  latitude: number | null,
  longitude: number | null,
  isPreciseLocation: boolean,
  previousPrice?: number;
  priceReductionPercentage?: number;
  isEligibleForPriceReduction?: boolean;
  featuredProperty?: { fieldName?: string; label?: string; score?: number };
  settlementId?: string;
  // Location names returned next to the ids (summary and detail responses)
  countryName?: string | null;
  countyName?: string | null;
  cityName?: string | null;
  settlementName?: string | null;
  // deepest known of city / county / country; for ads with a wider
  // locationScope (2 whole-croatia / 3 counties / 4 cities) BE builds this
  // as a comma-separated list of the picked areas
  locationName?: string | null;
  // V2OGLASI-2648: 1=exact (default, existing ads), 2=whole Croatia,
  // 3=picked counties, 4=picked cities/municipalities
  locationScope?: number;
  // Picked county/city entries for scope 3/4, empty otherwise
  locationAreas?: Array<{ id: string; name: string }>;
  // Form-side ids for the picked areas (edit flow)
  locationAreaIds?: string[];
  // Settlement coordinates (detail response), fallback position for the map
  settlementLatitude?: number | null;
  settlementLongitude?: number | null;
  // Make / model and subcategory names and url slugs (detail response)
  makeName?: string | null;
  makeRoute?: string | null;
  modelName?: string | null;
  modelRoute?: string | null;
  categoryLv1Name?: string | null;
  categoryLv1Route?: string | null;
  categoryLv2Name?: string | null;
  categoryLv2Route?: string | null;
  smartLink?: string;
  rentPriceType?: number;
  priceType?: number;
  status: any;
  makeId?: string;
  modelId?: string;
  makeYear?: string;
  // BE flags the ad with the primary (make, model) of any matching info entry
  // (direct or via variants). Null/absent means no info page exists.
  infoPage?: { makeId: string; modelId: string } | null;
  summary: { makeYear: string; mileage: number; isNew: number; power: number };
  sharingConfiguration: {
    createdAt: string;
    description: string;
    header: string;
    id: string;
    imageId: string | null;
    isDeleted: boolean;
    lastModifiedDate: string;
  };
  title?: string;
  videoUrl?: string;
  viewCount?: number;
  searchedViewCount?: number;
  favoriteCount?: number;
  redirectData: {
    cattegoryLv1Id?: string;
    cattegoryLv2Id?: string;
    category?: string;
    module?: string;
    smartLink?: string;
    code?: number;
    status?: number;
  } | null;
  permutiveData: PermutiveDataI;
  note?: string | null;
  hasFreePromotion?: boolean;
  searchPagePosition?: number | null;
}

export interface PermutiveDataI {
  adCode: number;
  module: string | null;
  categories: string[] | null;
  location: string | null;
  priceFrom: number | null;
  priceTo: number | null;
  eventType: string; // PermutiveEvents enum
}

// configuration/modules
export interface ModulesI {
  count?: number;
  displayName?: string;
  iconId?: string;
  name?: string;
  // url slug (auto-moto, nekretnine, ...)
  route?: string;
  is18Old?: boolean;
}

// configuration/categories
export interface CategoriesI {
  count?: number;
  displayName?: string;
  iconId?: string;
  moduleName?: string;
  moduleRoute?: string;
  name?: string;
  // url slug (osobni-automobili, ...)
  route?: string;
  is18Old?: boolean;
}

export interface RouteLinksI {
  link?: string;
  title?: string;
  // detail field the link comes from (makeId, categoryLv1Id, ...); unset for module and category
  field?: string;
}

export interface SpecialSectionConfigsI {
  dataId?: string;
  label?: string;
  dataSource?: any;
  field?: string;
  iconRelativeUrl?: string;
  numberFormatted?: boolean;
  title?: string;
  tooltip?: string;
  type?: string;
  unit?: string;
  value?: any;
}

export interface ConfigGroupI {
  attributeGroups?: {
    attributes?: { field?: string; title?: string }[];
    name?: string;
  }[];
  attributes?: {
    field?: string;
    numberFormatted?: boolean;
    title?: string;
    type?: string;
    unit?: string;
  }[];
  icon?: string;
  latitudeField?: string;
  longitudeField?: string;
  name?: string;
  type?: string;
}

export interface AttributeGroupsI {
  attributes?: AttributeI[];
  name?: string;
}

export interface AttributeI {
  field?: string;
  title?: string;
}

export interface ReportAdDataI {
  adId?: string;
  description?: string;
  reason?: number;
}

export type PopularModulesI = ModulesI;

export type PopularCategoriesI = CategoriesI;

// configuration/populars
export interface PopularsI {
  category?: string | null;
  categoryRoute?: string | null;
  count?: number;
  displayName?: string;
  iconId?: string;
  module?: string;
  moduleRoute?: string;
  gradientFrom?: string;
  gradientTo?: string;
}

export interface UserStatisticI {
  registeredUserCount?: number;
  tradeCount?: number;
}

export interface AdStatisticI {
  activeAdCount?: number;
  newActiveAdCount?: number;
}

export interface UserAdsStatisticsI {
  activeAdCount: number;
  deletedAdCount: number;
  soldAdCount: number;
  renewedAdCount: number;
  expiredAdCount: number;
  totalSearchedViewCount: number;
  totalViewCount: number;
  maxViewsAd: {
    code: number;
    viewCount: number;
    title: string;
  } | null;
  mostViewedAds: Array<{
    code: number;
    viewCount: number;
    title: string;
  }>;
  totalPromotedSearchedViewCount: number;
  totalPromotedViewCount: number;
  maxViewsPromotedAd: {
    code: number;
    viewCount: number;
    title: string;
  } | null;
  mostViewedPromotedAds: Array<{
    code: number;
    viewCount: number;
    title: string;
  }>;
  totalFavoriteCount: number;
  maxFavoriteCountAd: {
    code: number;
    favoriteCount: number;
    title: string;
  } | null;
  phoneClickCount: number;
  chatCount: number;
  sellerPageOpenedCount: number;
}

export interface SubLevelResourceChildrenI {
  id?: string;
  logoId?: string;
  logoOrder?: number;
  name?: string;
  route?: string;
  popular?: boolean;
  children?: {
    id?: string;
    name?: string;
    route?: string;
    filterOnly?: boolean;
  }[];
}

export interface BuildSubLevelPayloadI {
  levelId?: string;
}

export interface ParseAdDetailLevelI {
  category?: string;
  creator?: ParseCreatorI;
  details?: AdDetailI[];
  module?: string;
  routeLinks?: RouteLinksI[];
  specialAttributes?: [];
  summary?: PassedAdI[];
}

export interface ParseCreatorI {
  additionalCellPhone?: string | null;
  avatar?: string | null;
  cellPhone?: string;
  cityId?: string;
  countryId?: string;
  countyId?: string;
  description?: string | null;
  email?: string;
  id?: string;
  phoneNumber?: string;
  registrationDate?: string;
  username?: string;
}

export interface AdDetailI {
  data?: AdDetailDataI[];
  name?: string;
  type?: string;
}

export interface AdDetailDataI {
  title?: string;
  value?: string;
}

export interface EmailDataI {
  adId?: string;
  email?: string;
  message?: string;
}

export interface AdListI {
  count?: number;
  currentPage?: number;
  data?: PassedAdI[];
  nextPage?: number;
}

// *****************************************************************************************************************

export interface ControlOptionI {
  field: string;
  title: string;
  value: number;
}

export interface AdI {
  adType: number;
  category: string;
  cityId: string;
  countryId: string;
  countyId: string;
  legalEntity: number;
  module: string;
  price: number;
  priceTo: number;
  settlementId: string;
  title: string;
}


export interface AdFormConfig {
  name?: string;
  categories?: Category[];
}

export interface Category {
  name?: string;
  nameHr?: string;
  steps?: StepControlI[];
}

export interface Step {
  name?: string;
  description?: string;
  groups?: Group[];
}

export interface StepControlI extends Omit<Step, "groups"> {
  controls?: ControlI[];
}

export interface Group {
  controls?: ControlI[];
  fullWidth?: boolean;
  link?: string;
  name?: string;
}

export interface ControlI {
  addLabel?: string;
  child?: ControlChildI;
  children: any;
  condition?: string;
  currencyField?: string;
  dataID?: string;
  dataSource?: string[];
  defaultChecked?: boolean;
  defaultCurrency?: string;
  defaultValue?: number;
  excluded?: boolean;
  field?: string;
  fromField?: string;
  fromTagFormat?: string;
  id: string;
  isDateValue?: boolean;
  isRequired?: boolean;
  latitudeField: string;
  logoTitle?: string;
  longitudeField: string;
  max?: number;
  maxItems?: number;
  maxPhotoNumber?: number;
  min?: number;
  numberFormatted?: boolean;
  options?: Option[];
  placeholder?: string;
  regex?: string;
  regexMessage?: string;
  showAllLevel?: boolean;
  // make picker: ad field holding the subcategory the makes are narrowed to (categoryLv1Id)
  subcategoryField?: string;
  title?: string;
  toField?: string;
  toTagFormat?: string;
  type?: string;
  unit?: string;
  valueType?: string;
}

export interface ControlChildI {
  child?: ControlChildI;
  chosenValue?: string;
  excludedField?: any;
  field?: string;
  placeholder?: string;
  title?: string[];
}

export interface Option {
  field?: string;
  title?: string;
  value?: boolean | number;
  color?: string;
}

export interface UpdateDataI {
  adCode: number;
  adId: string;
  formData: FormDataI;
  user: UserI;
}

export interface FormDataI {
  adType: number;
  category: string;
  categoryId: number;
  code: number;
  countryId: string;
  countyId: string;
  creatorEmail: string;
  creatorId: string;
  description: string;
  expirationPeriod: 30;
  externalUrl: string;
  id: string;
  images: string[];
  is18Old: false;
  isAccidentDamagedVehicle: false;
  isAccidentVehicle: false;
  isDamaged: false;
  isFavorite: false;
  isNew: number;
  legalEntity: number;
  makeId: string;
  makeYear: string;
  modelId: string;
  module: string;
  paymentOption: number[];
  postedTime: string;
  price: number;
  sharingConfiguration: SharingConfigurationI;
  smartLink: string;
  title: string;
  videoUrl: string;
  viewCount: number;
  searchedViewCount: number;
  favoriteCount: number;
}

export interface SharingConfigurationI {
  description: string;
  header: string;
  imageId: string;
}

export interface currentValues {
  makeId: string;
  modelId: string;
}

// *****************************************************************************************************************

export interface AttributeI {
  dataId?: string;
  dataSource?: string;
  field?: string;
  numberFormatted?: boolean;
  options?: {
    title?: string;
    value?: number;
  }[];
  searchField?: string;
  title?: string;
  type?: string;
  unit?: string;
  valueType?: string;
}

export interface AttributeGroupI {
  attributes?: {
    field?: string;
    title?: string;
  }[];
  name?: string;
}

export interface GroupAttributeI {
  data?: NoValueAttributeI[];
  field?: string;
  numberFormatted?: boolean;
  title?: string;
  type?: string;
  unit?: string;
}

export interface NoValueAttributeI {
  data?: NoValueAttributeDataI[];
  dataId?: any;
  dataSource?: any;
  field?: string;
  title?: string;
  value?: any;
}

export interface NoValueAttributeDataI {
  dataId?: any;
  dataSource?: any;
  value?: any;
}

// *****************************************************************************************************************

export interface PassedAdListI {
  favorites: AdListFavoriteI;
}

export interface AdListFavoriteI {
  count: number;
  data: AdListFavoriteDataI[];
  page: number;
}

export interface AdListFavoriteDataI {
  category: string;
  code: number;
  id: string;
  images: string[];
  isDeleted: undefined;
  isFavorite: true;
  isRead: true;
  link: string;
  location: string;
  module: string;
  postedTime: string;
  price: number;
  priceTo: number;
  status: number | undefined;
  summary: [];
  title: string;
  videoUrl: string | null;
  viewCount: number;
  searchedViewCount: number;
  favoriteCount: number;
  rentPriceType?: number;
  priceType?: number;
}

// *****************************************************************************************************************

export interface CommonDataI {
  child?: {
    child?: { title?: string; field?: string; child?: {} };
    chosenValue?: string;
    field?: string;
    title?: string;
  };
  dataId?: string;
  dataSource?: string;
  field?: string;
  id?: string;
  isMultiRows?: boolean;
  tagFormat?: string;
  title?: string;
  type?: string;
}

export interface ControlFieldsI {
  includeCityIds?: CommonDataI;
  includeCountryIds?: CommonDataI;
  includeCountyIds?: CommonDataI;
  includeSettlementIds?: CommonDataI;
  priceFrom?: PriceI;
  priceTo?: PriceI;
  viewViaVideoCall?: ViewI;
  withPicture?: ViewI;
}

export interface BasicOptionI {
  field?: any;
  title?: string;
  value?: number;
}

export interface PriceI {
  id?: string;
  isDateValue?: boolean | undefined;
  numberFormatted?: boolean;
  tagFormat?: string;
  type?: string;
}

export interface ViewI {
  defaultChecked?: boolean;
  field?: string;
  id?: string;
  tagFormat?: string;
  title?: string;
  type?: string;
}

export interface ControlConfigI {
  defaultChecked?: boolean;
  field?: string;
  tagFormat?: string;
  title?: string;
  type?: string;
}

export type GetSaveSearchCategoryI = CategoriesI;

export type GetSaveSearchModuleI = ModulesI;

export interface QueryParamsI {
  category?: string;
  module?: string;
  sortOption?: number;
  text?: string;
}

export interface SaveSearchI {
  category?: string;
  criteria?: { module?: number; sortOption?: string };
  module?: string;
  name?: string;
  notifyFrequency?: number;
  text?: string;
}

export interface SavedSearchI {
  count?: number;
  currentPage?: number;
  data?: SavedSearchDataI[];
  nextPage?: number;
}

export interface SavedSearchDataI {
  category?: string;
  createdTime?: string;
  criteria?: { category?: string };
  formattedCreatedTime?: string;
  id?: string;
  imageId?: string;
  module?: string;
  name?: string;
  notifyFrequency?: number;
  text?: string;
}

export interface SearchConfigI {
  categories?: SearchConfigCategoryI[];
  name?: string;
  nameHr?: string;
}

export interface SearchConfigCategoryI {
  groups?: CategoryGroupI[];
  name?: string;
  nameHr?: string;
}

export interface CategoryGroupI {
  controls?: GroupControlsI[];
  name?: string;
}

export interface GroupControlsI {
  controls?: CommonDataI[];
  name?: string;
}



// WEBPACK FOOTER //
// ./src/typings/routesTypes.ts